import { E3FavoriteSelected, E3FavoriteStar, F1Group, J2Documentcopy, J2Download, K1Safety, K1View } from '@e3dc-react/icons'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { format } from '@e3dc-react/shell/Libs/date'
import { Box, Stack, IconButton, Tooltip } from '@mui/material'
import React from 'react'
import { DownloadFileItem, DownloadsCategoryModel, DownloadsSearchResultParent } from 'src/Libs/downloads/models/downloads.model'
import UserLevelTag from './UserLevelTag'
import { removeFileFavorite, setFileFavorite } from '../api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import EditDownloadFileButton from './EditDownloadFileButton'
import DownloadItemBreadcrumbs from './search-file/DownloadItemBreadcrumbs'
import { useDownloadItemActions } from '../hooks'
import { useCopyToClipboard } from 'src/Elements/CopyToClipboard/hook'
import { useDispatch } from 'react-redux'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { isAndroid } from '@e3dc-react/shell/Configuration'
import { useIsWebviewMode } from 'src/Hooks'
import { ALLOWED_FILE_TYPES } from '../consts'

interface DocumentNodeProps {
  file: DownloadFileItem
  isServiceUser?: boolean
  showBreadcrumbs?: boolean
  selectCategory: (categoryId?: number) => void
  searchResultParents?: DownloadsSearchResultParent[]
  directParent?: DownloadsCategoryModel
  favoriteFileIds: number[]
}

/**
 * DocumentNode component displays a single document in the downloads list
 */
const DocumentNode: React.FC<DocumentNodeProps> = ({
  file,
  searchResultParents,
  isServiceUser,
  showBreadcrumbs = false,
  selectCategory,
  directParent,
  favoriteFileIds,
}) => {
  const { t } = useCustomTranslation()
  const { viewFile, downloadFile } = useDownloadItemActions()
  const { copyToClipboard } = useCopyToClipboard()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const dispatch = useDispatch()
  const isViewButtonEnabled = ALLOWED_FILE_TYPES.includes(file.fileType?.toLowerCase() ?? '')
  const firstGridRowIndex = showBreadcrumbs ? 1 : 0
  const isWebview = useIsWebviewMode()
  const hidePreviewButton = isWebview && isAndroid

  const handleCopyToClipboard = async (): Promise<void> => {
    const previewUrl =
      file.access === 'public'
        ? `${process.env.REACT_APP_PUBLIC_FILES_DOMAIN}/${file.urlName}`
        : `${process.env.REACT_APP_BASEURL}/downloads?file=${file.urlName}`
    copyToClipboard(previewUrl)
  }

  const handleFavoriteClick = async (): Promise<void> => {
    try {
      const fileResult = await setFileFavorite(file.id, authFetch)

      if (!fileResult.hasErrors) {
        dispatch(refreshApiView())
        enqueueSuccessStack(t('downloads.file-favorite-set.success'))
      } else enqueueErrorStack(t('downloads.file-favorite-set.error'))
    } catch (error) {
      console.error('Error setting file favorite', error)
      enqueueErrorStack(t('downloads.file-favorite-set.error'))
    }
  }

  const removeFavorite = async (): Promise<void> => {
    try {
      const fileResult = await removeFileFavorite(file.id, authFetch)
      if (!fileResult.hasErrors) {
        dispatch(refreshApiView())
        enqueueSuccessStack(t('downloads.file-favorite-remove.success'))
      } else enqueueErrorStack(t('downloads.file-favorite-remove.error'))
    } catch (error) {
      console.error('Error setting file favorite', error)
      enqueueErrorStack(t('downloads.file-favorite-remove.error'))
    }
  }

  const getAccessIcon = (): React.ReactElement => {
    if (file.access === 'public') {
      return (
        <Tooltip title={t('files.download-item.public-access-tooltip')}>
          <IconButton size="small" onClick={handleCopyToClipboard}>
            <F1Group />
          </IconButton>
        </Tooltip>
      )
    } else {
      return (
        <Tooltip title={t('files.download-item.protected-access-tooltip')}>
          <IconButton size="small" onClick={handleCopyToClipboard}>
            <K1Safety />
          </IconButton>
        </Tooltip>
      )
    }
  }

  const renderFavoriteStar = (): React.ReactElement => {
    if (!favoriteFileIds?.includes(file.id)) {
      return (
        <IconButton onClick={handleFavoriteClick}>
          <E3FavoriteStar />
        </IconButton>
      )
    } else {
      return (
        <IconButton onClick={removeFavorite}>
          <E3FavoriteSelected />
        </IconButton>
      )
    }
  }

  return (
    <Box
      data-qa={`file-download-${file.id}`}
      sx={{
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'grey.grey06',
        pl: 4,
        pr: 2,
        py: 2,
        flex: 1,
        justifyContent: 'space-between',
        borderRadius: 1,
        minHeight: 68,
        backgroundColor: 'background.paper',
        display: 'grid',
        alignItems: 'center',
        gridTemplateColumns: { xs: 'auto 1fr 30px', sm: '30px 1fr 1fr 110px', md: '30px 2fr 1fr 110px' },
        gridTemplateRows: showBreadcrumbs ? { xs: 'repeat(5, 1fr)', sm: '2fr' } : { xs: 'repeat(4, 1fr)', sm: '2fr' },
        columnGap: 2,
        rowGap: { xs: 0, sm: 1 },
      }}
    >
      {showBreadcrumbs && (
        <Box sx={{ gridColumn: { xs: '1 / 4', sm: '1 / 5' }, gridRow: '1' }}>
          <DownloadItemBreadcrumbs parentCategories={searchResultParents ?? []} onClickCategory={selectCategory} />
        </Box>
      )}
      <Box
        sx={{
          gridColumn: '1',
          gridRow: { xs: `${firstGridRowIndex + 1}`, sm: `${firstGridRowIndex + 1} / ${firstGridRowIndex + 3}` },
          alignItems: 'center',
          display: 'flex',
        }}
      >
        <J2Documentcopy />
      </Box>
      <Box
        sx={{
          gridColumn: '2',
          gridRow: `${firstGridRowIndex + 1}`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="h3" noWrap>
          {file.fileName ?? ''}
        </Typography>
        {file.urlName && getAccessIcon()}
      </Box>
      <Box sx={{ gridColumn: { xs: '1 / 3', sm: '2' }, gridRow: `${firstGridRowIndex + 2}`, overflow: 'hidden' }}>
        <Typography
          variant={'body2'}
          noWrap
          values={{
            extension: file.fileType?.toUpperCase() ?? '',
            language: t(`downloads.language.${file.language}`),
            date: file.updatedAt ? format(new Date(file.updatedAt), t('common.format.date-fns-date')) : '',
          }}
        >
          {'files.download-item.file-details'}
        </Typography>
      </Box>
      <Box
        sx={{ gridColumn: { xs: '1 / 3', sm: '3' }, gridRow: { xs: `${firstGridRowIndex + 4}`, sm: `${firstGridRowIndex + 1}` }, overflow: 'hidden' }}
      >
        {isServiceUser && <UserLevelTag isVisibleInstallerPartner={file.visibleToInstaller} isVisibleUser={file.visibleToCustomer} />}
      </Box>
      <Box
        sx={{ gridColumn: { xs: '1 / 3', sm: '3' }, gridRow: { xs: `${firstGridRowIndex + 3}`, sm: `${firstGridRowIndex + 2}` }, overflow: 'hidden' }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {file.countryList?.map(country => (
            <Typography key={country} variant="body2">
              {country}
            </Typography>
          ))}
        </Stack>
      </Box>
      <Box
        sx={{
          gridColumn: { xs: '3', sm: '4' },
          gridRow: { xs: `${firstGridRowIndex + 1} / ${firstGridRowIndex + 5}`, sm: `${firstGridRowIndex + 1} / ${firstGridRowIndex + 3}` },
          justifySelf: 'end',
          alignItems: 'center',
        }}
      >
        <Stack id={'button-group'} direction={{ xs: 'column', sm: 'row' }} alignItems={'center'}>
          {!isServiceUser && renderFavoriteStar()}
          <IconButton onClick={() => downloadFile(file.id)}>
            <J2Download />
          </IconButton>
          {!hidePreviewButton && (
            <Tooltip title={t('files.download-item.view-file-tooltip')} disableHoverListener={isViewButtonEnabled}>
              {/* Note this Box is needed for disabled buttons to show a tooltip on safari see https://mui.com/material-ui/react-tooltip/#disabled-elements */}
              <Box>
                <IconButton disabled={!isViewButtonEnabled} onClick={() => viewFile(file.id)}>
                  <K1View />
                </IconButton>
              </Box>
            </Tooltip>
          )}
          {isServiceUser && <EditDownloadFileButton item={file} selectCategory={selectCategory} parent={directParent} />}
        </Stack>
      </Box>
    </Box>
  )
}

export default DocumentNode
